// cspell:ignore TSTDRV -- NetSuite Test Drive account id prefix.
import { NetSuiteRecordCurrency as GeneratedNetSuiteRecordCurrency } from "@redotech/congo-enums/net-suite-record-currency";
import { createEnum } from "@redotech/zod-util/zod-enum";
import {
  ALLOWED_DYNAMIC_VARIABLES_ON_RMA_BODY_LEVEL,
  type RmaBodyLevelVariableMap,
} from "../dynamic-variables/rma-body-level";

export enum NetsuiteCloseReturnAuthorizationPermissionType {
  CLOSE = "close",
  AUTOMATION = "automation",
}

/**
 * Which currency the amounts on records we create/adjust (return authorization,
 * credit memo, cash refund) should be represented in. We only act when the
 * originating NetSuite transaction's currency matches the selected source;
 * otherwise we leave amounts untouched. Absent = feature off.
 *
 * Values are sourced from the generated `net_suite_record_currency` CockroachDB
 * enum so they cannot drift from the schema; the keys are local aliases kept for
 * existing callers. See `docs/db-enums.md`.
 */
export const NetSuiteRecordCurrency = {
  SHOP: GeneratedNetSuiteRecordCurrency.Shop,
  PRESENTMENT: GeneratedNetSuiteRecordCurrency.Presentment,
} as const;
export type NetSuiteRecordCurrency =
  (typeof NetSuiteRecordCurrency)[keyof typeof NetSuiteRecordCurrency];

export const NetSuiteRecordCurrencyZodSchema = createEnum(
  NetSuiteRecordCurrency,
  { enumName: "NetSuiteRecordCurrency" },
);

/** How an exchange Sales Order receives the returned value: a pre-tax header discount, or an after-tax RMA credit memo applied to its invoice. */
export const NetSuiteExchangeReturnValueApplication = {
  HEADER_DISCOUNT: "header_discount",
  CREDIT_MEMO: "credit_memo",
} as const;
export type NetSuiteExchangeReturnValueApplication =
  (typeof NetSuiteExchangeReturnValueApplication)[keyof typeof NetSuiteExchangeReturnValueApplication];

export const NetSuiteExchangeReturnValueApplicationZodSchema = createEnum(
  NetSuiteExchangeReturnValueApplication,
  { enumName: "NetSuiteExchangeReturnValueApplication" },
);

/** Which NetSuite record a credit memo is built from. */
export const NetsuiteCreditMemoSource = {
  RMA: "rma",
  INVOICE: "invoice",
} as const;
export type NetsuiteCreditMemoSource =
  (typeof NetsuiteCreditMemoSource)[keyof typeof NetsuiteCreditMemoSource];
export const NetsuiteCreditMemoSources = [
  NetsuiteCreditMemoSource.RMA,
  NetsuiteCreditMemoSource.INVOICE,
] as const;

/**
 * Which NetSuite transaction the Return Authorization is created from, highest
 * priority first. Absent or empty means the historical behavior: a cash sale
 * for cash sale orders, the sales order otherwise.
 */
export const NetsuiteRmaSource = {
  SALES_ORDER: "salesOrder",
  CASH_SALE: "cashSale",
  INVOICE: "invoice",
} as const;
export type NetsuiteRmaSource =
  (typeof NetsuiteRmaSource)[keyof typeof NetsuiteRmaSource];
export const NetsuiteRmaSources = [
  NetsuiteRmaSource.SALES_ORDER,
  NetsuiteRmaSource.CASH_SALE,
  NetsuiteRmaSource.INVOICE,
] as const;

export const NetSuiteItemReceiptRestockDecisionSource = {
  NETSUITE_ITEM_RECEIPT_RESTOCK_FIELD: "netsuite_item_receipt_restock_field",
  REDO_INVENTORY_RESTOCK_SETTING: "redo_inventory_restock_setting",
} as const;
export type NetSuiteItemReceiptRestockDecisionSource =
  (typeof NetSuiteItemReceiptRestockDecisionSource)[keyof typeof NetSuiteItemReceiptRestockDecisionSource];
export const NetSuiteItemReceiptRestockDecisionSources = [
  NetSuiteItemReceiptRestockDecisionSource.NETSUITE_ITEM_RECEIPT_RESTOCK_FIELD,
  NetSuiteItemReceiptRestockDecisionSource.REDO_INVENTORY_RESTOCK_SETTING,
] as const;
export const NetSuiteItemReceiptRestockDecisionSourceZodSchema = createEnum(
  NetSuiteItemReceiptRestockDecisionSource,
  { enumName: "NetSuiteItemReceiptRestockDecisionSource" },
);

export type FieldValueType = "string" | "number" | "boolean";

export interface NetSuiteCustomFieldValueMapping {
  sourceValue: string;
  targetValue: string;
}

export interface NetSuiteCustomField {
  fieldKey: string;
  fieldValue: string | NetSuiteCustomField[];
  valueType?: FieldValueType;
  valueMappings?: NetSuiteCustomFieldValueMapping[];
}

const BOOLEAN_DYNAMIC_VARIABLES_ON_NETSUITE_RMA_BODY_LEVEL = [
  "isPosReturn",
  "hasDamagedItems",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_NETSUITE_RMA_BODY_LEVEL = [
  ...ALLOWED_DYNAMIC_VARIABLES_ON_RMA_BODY_LEVEL,
  ...BOOLEAN_DYNAMIC_VARIABLES_ON_NETSUITE_RMA_BODY_LEVEL,
] as const;

export type NetSuiteRmaBodyLevelVariableMap = RmaBodyLevelVariableMap &
  Record<
    (typeof BOOLEAN_DYNAMIC_VARIABLES_ON_NETSUITE_RMA_BODY_LEVEL)[number],
    boolean
  >;

export const ALLOWED_DYNAMIC_VARIABLES_ON_LINE_ITEM = [
  "primaryReturnReason",
  "secondaryReturnReason",
  "primaryReasonCode",
  "secondaryReasonCode",
  "isGreenReturn",
  "compensationType",
  "refundAmount",
  "storeCreditAmount",
  "lineItemId",
  "comments",
  "trackingNumber",
  "carrierName",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_CREDIT_MEMO = [
  "ecommerceGiftCardId",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_CASH_REFUND = [
  "ecommerceGiftCardId",
  "parentOrderId",
  "exchangeOrderId",
  "stripeUpsellRefundId",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_CUSTOMER_REFUND = [
  "shouldRestock",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_RATE_AMOUNT_TEMPLATE = [
  "exchangeCredit",
  "storeCreditAmount",
  "deductedShippingFee",
  "finalizeFlowAdjustment",
  "refundToGiftCardAmount",
] as const;

export const NETSUITE_ACCOUNT_ID_FORMAT_ERROR_MESSAGE =
  "Non-production account IDs must use an uppercase, underscore-prefixed environment suffix: `_SB<number>` for sandbox (e.g. `1234567_SB1`, `1234567_SB2`) or `_RP` / `_RP<number>` for release preview (e.g. `1234567_RP`, `1234567_RP1`). Developer and demo accounts are `TD` or `TSTDRV` followed by digits (e.g. `TD3062033`).";

/**
 * SuiteCloud Developer Network demo and developer accounts are letter-prefixed
 * rather than numeric, and take the same environment suffix.
 */
const VALID_NETSUITE_ACCOUNT_ID = /^(?:\d+|TD\d+|TSTDRV\d+)(?:_SB\d+|_RP\d*)?$/;

export function getNetSuiteAccountIdErrorMessage(
  accountId: string,
): string | undefined {
  const trimmed = accountId.trim();
  if (trimmed === "") {
    return undefined;
  }
  if (VALID_NETSUITE_ACCOUNT_ID.test(trimmed)) {
    return undefined;
  }
  return NETSUITE_ACCOUNT_ID_FORMAT_ERROR_MESSAGE;
}
